import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import TranscriberForm from "components/AgentWorkspace/TranscriberForm";
import BypassTranscriptionSummary from "components/AgentWorkspace/BypassTranscriptionSummary";
import { useParams } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";
import Alert from "react-bootstrap/Alert";
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import Spinner from "react-bootstrap/Spinner";
import { getErrorMessage } from "services/UtilityService";
import { showNotification } from "services/NotificationService";
import ConfigurablePdfViewer from "components/PdfViewer/ConfigurablePdfViewer";
import { convertToComponentStructureResponse } from "services/transcriber/transcriberDetailsUtils";
import {
  buildTranscriberDetailsApiPath,
  buildTranscriberPdfApiPath,
  TRANSCRIBER_PROGRAM_CODE,
} from "lookup/TranscriberProgramConstants.jsx";
import { getTranscriberDetailsConfig } from "lookup/TranscriberDetailsConfig";

const DMR_PROGRAM_TYPE = TRANSCRIBER_PROGRAM_CODE.DMR;
const DMR_DETAILS_CONFIG = getTranscriberDetailsConfig(DMR_PROGRAM_TYPE);

const DmrTranscribeDetails = () => {
  const { programTranscriptionInfoUuid } = useParams();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorStatus, setErrorStatus] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [responseType, setResponseType] = useState([]);
  const [bypassSummary, setBypassSummary] = useState(null);

  const getTranscriptionData = useCallback((id) => {
    setLoading(true);
    setErrorStatus(false);
    setErrorMessage("");
    setBypassSummary(null);
    setResponseType([]);
    setFormData(null);
    sendAuthenticatedAsyncRequest(
      buildTranscriberDetailsApiPath(DMR_PROGRAM_TYPE, id),
      "GET",
      null,
      (r) => {
        if (r.data?.isBypassedAutoDisposition) {
          setBypassSummary(r.data);
          setLoading(false);
          return;
        }
        if (r.data.responseType) {
          setResponseType(r.data.responseType);
        }
        setFormData(convertToComponentStructureResponse(r.data));
        setLoading(false);
      },
      (r) => {
        setLoading(false);
        setErrorStatus(true);
        setErrorMessage("unable to fetch transcription form");
        showNotification({
          title: "Error!",
          message:
            getErrorMessage(r.data) || getErrorMessage("Unable to get the form"),
          position: "tr",
          type: "error",
        });
      }
    );
  }, []);

  useEffect(() => {
    if (programTranscriptionInfoUuid) {
      getTranscriptionData(programTranscriptionInfoUuid);
    }
  }, [programTranscriptionInfoUuid, getTranscriptionData]);

  const renderLeftPanel = () => {
    if (bypassSummary) {
      return (
        <BypassTranscriptionSummary
          transcriptionIdLabel={DMR_DETAILS_CONFIG.transcriptionIdAlertLabel}
          transcriptionId={programTranscriptionInfoUuid}
          isBypassedAutoDisposition={bypassSummary.isBypassedAutoDisposition}
          disposition={bypassSummary.disposition}
          associatedSwitchId={bypassSummary.associatedSwitchId}
        />
      );
    }

    if (responseType && responseType.length > 0) {
      return responseType.map((type, index) => (
        <Alert key={index} variant="danger">
          Response Type: {type}
        </Alert>
      ));
    }

    return (
      <>
        <Alert variant="primary">
          {DMR_DETAILS_CONFIG.transcriptionIdAlertLabel}:{" "}
          {programTranscriptionInfoUuid}
        </Alert>
        <TranscriberForm
          detailsConfig={DMR_DETAILS_CONFIG}
          formData={formData}
        />
      </>
    );
  };

  if (
    !loading &&
    !errorStatus &&
    (formData || bypassSummary || responseType.length > 0)
  ) {
    return (
      <Container fluid>
        <Row>
          <Col md={5}>{renderLeftPanel()}</Col>
          <Col md={7}>
            <ConfigurablePdfViewer
              height="85vh"
              width="100%"
              url={buildTranscriberPdfApiPath(
                DMR_PROGRAM_TYPE,
                programTranscriptionInfoUuid
              )}
            />
          </Col>
        </Row>
      </Container>
    );
  } else if (errorStatus) {
    return (
      <Alert key="error" variant="danger">
        {errorMessage}
      </Alert>
    );
  } else {
    return <Spinner animation="border" role="status" />;
  }
};

export default connect()(DmrTranscribeDetails);
